/**
 * The Forma tab — canon `docs/design/analiza-wideo-2026-09-23/`, without a plan line, a hit or
 * miss, or a download. Every decision is `model/`'s; this file places the columns and binds
 * gestures. Three views: the exercise (where the tab opens), the five-week matrix, and „Nagrania",
 * the gallery. Every way into a film calls `onOpenClip`; the tab stays mounted under the player,
 * so closing it finds the same view, exercise, A, B, Linia and panel.
 *
 * The list, the centre and the head hold their places across the three views, so the switch keeps
 * its focus; each view's gate is a `useViewGate`, because its boxes mount afresh on every switch.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LiftLine } from '~/entities/lift-history';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useForma } from '../lib/useForma';
import { useFormaGallery } from '../lib/useFormaGallery';
import { useFormaMatrix, usePrefetchFormaMatrix } from '../lib/useFormaMatrix';
import { useRecordingRequest } from '../lib/useRecordingRequest';
import { useViewGate } from '../lib/useViewGate';

import styles from './DetailTab.module.css';
import { usePublishTabMeta } from './detailTabMeta';
import forma from './Forma.module.css';
import { FormaDock } from './FormaDock';
import { FormaExerciseList } from './FormaExerciseList';
import { FormaGallery } from './FormaGallery';
import { FormaHead, type FormaMode } from './FormaHead';
import { FormaMatrix } from './FormaMatrix';
import { FormaSessionTable } from './FormaSessionTable';
import { FormaStage } from './FormaStage';
import { toggleAid } from './TwoDatesStageParts';
import { TwoDatesTable } from './TwoDatesTable';

interface FormaTabProps {
  readonly clientId: number;
  readonly reducedMotion: boolean;
  /** The clip the player shows, when the tab was reached through one. */
  readonly openClipId: number | null;
  /** Open the full player on a recording, at a second when the panel's film was left there. */
  readonly onOpenClip: (clipId: number, startAt?: number | null) => void;
}

export function FormaTab({ clientId, reducedMotion, openClipId, onOpenClip }: FormaTabProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<FormaMode>('one');
  const [galleryExercise, setGalleryExercise] = useState<number | null>(null);
  const [line, setLine] = useState<LiftLine>('e1rm');
  const [trend, setTrend] = useState(true);
  const [projection, setProjection] = useState(false);

  const state = useForma(clientId, { line, trend, projection, openClipId });
  const matrix = useFormaMatrix(clientId, mode === 'all');
  const prefetchMatrix = usePrefetchFormaMatrix(clientId);
  const request = useRecordingRequest(clientId, state.exercise?.exerciseId ?? null);
  const gallery = useFormaGallery(clientId, state.list, galleryExercise, mode === 'clips');
  usePublishTabMeta(null);

  const { list, view, panel } = state;
  const noExercises = !list.pending && !list.failed && list.all.length === 0;
  const failed = list.failed || state.failed;
  const listGate = useViewGate(list.pending, mode !== 'all');
  const gate = useViewGate(list.pending || (!list.failed && !noExercises && state.pending), mode === 'one');
  const matrixGate = useViewGate(matrix.pending, mode === 'all');
  const galleryGate = useViewGate(gallery.pending, mode === 'clips');
  // Until the pin, the toggle or the newest recording sets the panel's width, the columns wait as one box.
  const reserve = mode === 'one' && gate.waiting && !panel.chosenByTrainer;
  const retry = list.failed ? list.retry : state.retry;
  const galleryRow =
    galleryExercise === null ? null : (list.all.find((row) => row.exerciseId === galleryExercise) ?? null);

  if (mode === 'one' && noExercises) {
    return (
      <div className={`${String(styles.tab)} ${listGate.reveal}`}>
        <EmptyState
          hint={t('web:forma.list.emptyHint')}
          mark={<DumbbellMarkIcon />}
          title={t('web:forma.list.empty')}
          tone="neutral"
        />
      </div>
    );
  }

  const head = {
    one: {
      cover: { url: state.exercise?.coverUrl ?? null },
      title:
        state.exercise === null ? (
          listGate.skeleton ? (
            <i className={`${String(skel.bar)} ${String(forma.skelTitle)}`} />
          ) : null
        ) : (
          state.exercise.name
        ),
      titleHint: state.exercise?.name,
      sub: gate.waiting || failed ? '' : view.headline,
      subReveal: gate.reveal,
    },
    all: {
      cover: null,
      title: t('web:forma.matrix.title'),
      titleHint: undefined,
      sub: t('web:forma.matrix.sub'),
      subReveal: '',
    },
    clips: {
      cover: galleryRow === null ? null : { url: galleryRow.coverUrl },
      title: galleryRow === null ? t('web:forma.gallery.title') : galleryRow.name,
      titleHint: galleryRow?.name,
      sub:
        galleryGate.waiting || gallery.failed
          ? ''
          : galleryRow === null
            ? t('web:forma.gallery.sub', { count: gallery.view.count })
            : t('web:forma.gallery.count', { count: gallery.view.count }),
      subReveal: galleryGate.reveal,
    },
  }[mode];

  const workspace =
    mode === 'all'
      ? forma.workspaceMatrix
      : mode === 'clips'
        ? forma.workspaceGallery
        : panel.open
          ? ''
          : forma.workspaceRail;

  return (
    <div className={`${String(styles.tab)} ${String(forma.scroller)}`}>
      <div className={`${String(forma.workspace)} ${String(workspace)}`}>
        {mode === 'all' ? null : (
          <FormaExerciseList
            failed={list.failed}
            gate={listGate}
            onBack={
              mode === 'clips' && galleryExercise !== null
                ? () => {
                    setGalleryExercise(null);
                  }
                : null
            }
            onRetry={list.retry}
            onSelect={mode === 'clips' ? setGalleryExercise : state.choose}
            onToggleWithClipOnly={list.toggleWithClipOnly}
            rows={list.rows}
            search={list.search}
            selected={mode === 'clips' ? galleryExercise : (state.exercise?.exerciseId ?? null)}
            withClipOnly={list.withClipOnly}
          />
        )}

        {reserve ? (
          <ColumnsReserve gate={gate} />
        ) : (
          <div className={`${String(forma.centre)} ${mode === 'all' ? String(forma.centreMatrix) : ''}`}>
            <FormaHead
              mode={mode}
              onMode={setMode}
              onPrefetchMatrix={prefetchMatrix}
              titleReveal={listGate.reveal}
              {...head}
            />

            {mode === 'all' ? (
              <FormaMatrix
                failed={matrix.failed}
                gate={matrixGate}
                onOpen={(exerciseId) => {
                  state.choose(exerciseId);
                  setMode('one');
                }}
                onRetry={matrix.retry}
                view={matrix.view}
              />
            ) : mode === 'clips' ? (
              <FormaGallery
                gallery={gallery}
                gate={galleryGate}
                narrowed={galleryExercise !== null}
                onOpenClip={onOpenClip}
              />
            ) : (
              <>
                <FormaStage
                  failed={failed}
                  gate={gate}
                  onLine={setLine}
                  onMove={state.move}
                  onRetry={retry}
                  onShowRecorded={state.showRecorded}
                  onToggle={toggleAid(setTrend, setProjection)}
                  projection={projection}
                  reducedMotion={reducedMotion}
                  trend={trend}
                  view={view}
                />

                <section
                  className={`${String(styles.card)} ${String(styles.tableCard)} ${String(forma.changeCard)}`}
                >
                  <TwoDatesTable
                    aHead={
                      view.markers === null
                        ? t('web:forma.stage.marker.a')
                        : t('web:clientDetail.body.columnA', { date: view.markers[0].dateLabel })
                    }
                    bHead={
                      view.markers === null
                        ? t('web:forma.stage.marker.b')
                        : t('web:clientDetail.body.columnB', { date: view.markers[1].dateLabel })
                    }
                    // Forma's scale is fixed, so the caption is true before the answer and holds its line.
                    caption={view.tableCaption}
                    failed={failed ? t('web:forma.stage.failed') : null}
                    layout="compact"
                    note={view.rowsNote}
                    reveal={gate.reveal}
                    rows={view.rows}
                    skeleton={gate.skeleton}
                    // One row per line the movement's fields promise, known before its sessions are.
                    skeletonRows={view.promised.rows}
                    title={view.tableTitle}
                    waiting={gate.waiting}
                  />
                </section>

                <FormaSessionTable failed={failed} gate={gate} onOpenClip={onOpenClip} view={view} />
              </>
            )}
          </div>
        )}

        {mode === 'one' && !reserve ? (
          <FormaDock
            clientId={clientId}
            dock={view.dock}
            exerciseName={state.exercise?.name ?? ''}
            failed={failed}
            gate={gate}
            onOpenClip={onOpenClip}
            onRetry={retry}
            panel={panel}
            playerOpen={openClipId !== null}
            rail={view.rail}
            request={request}
          />
        ) : null}
      </div>
    </div>
  );
}

/** The centre and the panel as one box while their width is unknown: the stage and table's shapes. */
function ColumnsReserve({ gate }: { readonly gate: SkeletonGate }) {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" className={forma.columnsReserve}>
      <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      {gate.skeleton ? (
        <>
          <i className={`${String(skel.bar)} ${String(forma.skelReserveHead)}`} />
          <i className={`${String(skel.bar)} ${String(forma.skelReserveStage)}`} />
          <i className={`${String(skel.bar)} ${String(forma.skelReserveTable)}`} />
        </>
      ) : null}
    </div>
  );
}
